import React, { useMemo, useState } from 'react';
import { Box, Text, Button, VStack, HStack, Circle, Collapse, Flex } from '@televet/kibble-ui';
import {
  ChannelMessageAttachmentEntityType,
  ChannelViewChannelMessageFragment,
  useGetServiceReminderNotificationQuery,
} from 'shared/types/graphql';
import { compareAsc, isBefore, parseISO, addMinutes } from 'date-fns';
import groupBy from 'lodash-es/groupBy';
import { formatDate, formatTime } from 'pages/Conversations/utils/formatDate';
import PetAvatar from 'shared/components/Avatars/PetAvatar';

interface ServiceReminderNotificationAttachmentProps {
  message: ChannelViewChannelMessageFragment;
}

const ServiceReminderNotificationAttachment = ({
  message,
}: ServiceReminderNotificationAttachmentProps): JSX.Element | null => {
  const [isShowingDetails, setIsShowingDetails] = useState(false);

  const onToggleDetails = (): void => {
    setIsShowingDetails((value) => !value);
  };

  const serviceReminderNotificationId =
    message.attachments?.[0]?.entityType === ChannelMessageAttachmentEntityType.ServiceReminderNotification
      ? message.attachments?.[0]?.entityId || ''
      : '';

  const { data } = useGetServiceReminderNotificationQuery({
    variables: { serviceReminderNotificationId },
    skip: !serviceReminderNotificationId,
  });

  const serviceReminderNotification = data?.findManyServiceReminderNotification?.[0];

  const serviceRemindersByPet = useMemo(() => {
    const mainReminder = serviceReminderNotification?.serviceReminder;

    if (!mainReminder) {
      return {};
    }

    const bundledReminders =
      serviceReminderNotification?.bundledNotifications.map(({ serviceReminder }) => serviceReminder) || [];
    const allReminders = [mainReminder, ...bundledReminders].sort((a, b) =>
      compareAsc(parseISO(a?.dueDate), parseISO(b?.dueDate)),
    );

    const grouped = groupBy(allReminders, (reminder) => reminder?.clinicPet?.id);
    Object.values(grouped).forEach((petReminders) => {
      petReminders.sort((a, b) => compareAsc(parseISO(a?.dueDate) || new Date(), parseISO(b?.dueDate) || new Date()));
    });

    return grouped;
  }, [serviceReminderNotification]);

  if (!serviceReminderNotificationId) {
    return null;
  }

  return (
    <Box className="ServiceReminderNotificationAttachment">
      <Flex
        w="100%"
        flex="0 0 auto"
        alignItems="stretch"
        justifyContent="center"
        my={8}
        fontSize="12px"
        className="ServiceReminderNotificationAttachment_Message"
      >
        <Box display="inline-block">
          <Text>
            Service reminder sent on{' '}
            {serviceReminderNotification?.queueAt
              ? `${formatDate(parseISO(serviceReminderNotification.queueAt))} at ${formatTime(
                  parseISO(serviceReminderNotification.queueAt),
                )}`
              : ''}
          </Text>
          <Button size="xs" variant="ghostNeutral" ml="1" verticalAlign="baseline" onClick={onToggleDetails}>
            {isShowingDetails ? 'Hide' : 'Show'} Details
          </Button>
        </Box>
      </Flex>
      <Collapse in={isShowingDetails} animateOpacity>
        <Box className="ServiceReminderNotificationAttachment__Details" bgColor="#fefaea" px="5" py="4">
          <VStack spacing="4" align="flex-start" pl="4" borderLeft="2px solid #dab307">
            {Object.entries(serviceRemindersByPet).map(([clinicPetId, reminders]) => {
              const clinicPet = reminders[0]?.clinicPet;
              return (
                <Box className="ServiceReminderNotificationAttachment__Details__PetSection" key={clinicPetId}>
                  <HStack spacing={0} mb={2}>
                    <Circle bgColor="background.subtle" size="6">
                      <PetAvatar
                        speciesType={clinicPet?.speciesType}
                        isDeceased={clinicPet?.computedIsDeceased}
                        isActive={clinicPet?.computedIsActive}
                        photoUrl={clinicPet?.photoUrl}
                        name={clinicPet?.name}
                        size="sm"
                      />
                    </Circle>
                    <Text fontWeight="bold">{clinicPet?.name}</Text>
                  </HStack>
                  {reminders.map((reminder) => {
                    if (!reminder) return null;
                    const dueDate = parseISO(reminder.dueDate);
                    const isPastDue = isBefore(dueDate, parseISO(serviceReminderNotification?.queueAt));
                    return (
                      <Box key={reminder.id}>
                        <Text>
                          {reminder.service.name} ({formatDate(addMinutes(dueDate, dueDate.getTimezoneOffset()))})
                          {isPastDue ? ' - Past Due' : ''}
                        </Text>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </VStack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ServiceReminderNotificationAttachment;
